import React, { useEffect, useState } from "react";
import axios from "axios";
import moment from "moment";
import { API_URL } from "../config.js";
import { emailApiClient, ensureEmailAuth, EMAIL_APIS_DISABLED } from "../email/api/client";
import { downloadOrViewAttachment } from "../email/api/hooks";
import { EmailBodyFrame } from "../email/components/EmailBodyFrame";
import "./emailViewPopup.css";

const formatDateTime = (date) => {
  if (!date) return "";
  const m = moment(date);
  return m.isValid() ? m.format("D MMM YYYY, hh:mm A") : String(date);
};

// "Name <email>" -> { name, email }
const parseSender = (raw) => {
  const match = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(raw || "");
  if (match) return { name: match[1] || match[2], email: match[2] };
  return { name: (raw || "").trim(), email: "" };
};

// Sent mails are stored as templates: show readable placeholders instead of raw variables
const readableTemplate = (html) =>
  (html || "")
    .replace(/\$userinfo->name/g, "Admin Name")
    .replace(/\$subscriber->name/g, "Client Name")
    .replace(/\$subscriber->email/g, "client@example.com")
    .replace(/\$subscriber->mobile/g, "9876543210")
    .replace(/\$[a-zA-Z0-9_]+->([a-zA-Z0-9_]+)/g, (match, fieldName) => {
      const readable = fieldName
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
      return `[${readable}]`;
    });

/**
 * Read-only view of one email from the subscriber's email box.
 *  - inbox: { type: "inbox", accountId, threadId, subject }  -> full mailbox conversation
 *  - sent:  { type: "sent", message_id, subject, from_email, sent_at, status } -> sent mail + delivery events
 */
const EmailViewPopup = ({ mail, canViewOriginal = false, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [thread, setThread] = useState(null);
  const [sentDetail, setSentDetail] = useState(null);

  const isInbox = mail?.type === "inbox";
  const mailKey = mail ? `${mail.type}-${mail.threadId || mail.message_id}` : "";

  useEffect(() => {
    if (!mail) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(false);
    setThread(null);
    setSentDetail(null);

    const load = async () => {
      try {
        if (mail.type === "inbox") {
          if (EMAIL_APIS_DISABLED) {
            if (!cancelled) {
              setThread(null);
              setLoading(false);
            }
            return;
          }
          // Same API the Email screen uses for its conversation view
          await ensureEmailAuth();
          const res = await emailApiClient.get(`/email/threads/${mail.threadId}`);
          if (!cancelled) setThread(res.data);
        } else {
          const token = localStorage.getItem("authToken");
          const res = await axios.get(
            `${API_URL}reports/email-statistics/${mail.message_id}/events`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (!cancelled) setSentDetail(res.data || {});
        }
      } catch (err) {
        console.error("Error loading email:", err);
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailKey]);

  useEffect(() => {
    if (!mail) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailKey]);

  if (!mail) return null;

  const originalUrl = isInbox
    ? `/email?accountId=${mail.accountId}&thread=${mail.threadId}`
    : `/Report/emailStatistics/${mail.message_id}?source=standard`;

  // Unsent drafts are not part of the conversation; newest message first, as on the Email screen
  const messages = (thread?.messages || [])
    .filter((m) => !m.labelIds?.includes("DRAFT"))
    .reverse();

  const sentEvents = sentDetail?.data || [];
  const sentTo = sentEvents[0]?.email;
  const subject =
    (isInbox ? thread?.subject : sentEvents[0]?.subject) || mail.subject || "(no subject)";

  return (
    <div
      className="modal fade show sub-email-popup"
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header">
            <div className="sub-email-title">
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>
                {subject}
              </h5>
              {isInbox && messages.length > 1 && (
                <span className="badge bg-secondary">{messages.length} messages</span>
              )}
            </div>
            <div className="sub-email-header-actions">
              {canViewOriginal && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => window.open(originalUrl, "_blank", "noopener,noreferrer")}
                >
                  View Original
                </button>
              )}
              <button type="button" className="btn btn-default" onClick={onClose} aria-label="Close">
                &times;
              </button>
            </div>
          </div>

          <div className="modal-body">
            {loading ? (
              <p className="text-center text-muted m-0 py-4">Loading email…</p>
            ) : error ? (
              <p className="text-center text-muted m-0 py-4">
                Couldn't load this email. Please try again.
              </p>
            ) : isInbox ? (
              messages.length === 0 ? (
                <p className="text-center text-muted m-0 py-4">No messages in this conversation.</p>
              ) : (
                messages.map((m) => {
                  const sender = parseSender(m.fromAddress);
                  return (
                    <div key={m.id} className="sub-email-message">
                      <div className="sub-email-message-head">
                        <div className="sub-email-people">
                          <div>
                            <strong>{sender.name || sender.email || "-"}</strong>
                            {sender.email && (
                              <span className="text-muted"> &lt;{sender.email}&gt;</span>
                            )}
                          </div>
                          <div className="text-muted sub-email-small">
                            to {m.toAddresses?.length > 0 ? m.toAddresses.join(", ") : "-"}
                          </div>
                          {m.ccAddresses?.length > 0 && (
                            <div className="text-muted sub-email-small">
                              cc {m.ccAddresses.join(", ")}
                            </div>
                          )}
                        </div>
                        <span className="text-muted sub-email-small sub-email-date">
                          {formatDateTime(m.internalDate)}
                        </span>
                      </div>

                      <EmailBodyFrame html={m.bodyHtml} text={m.bodyText} />

                      {m.attachments?.length > 0 && (
                        <div className="sub-email-attachments">
                          {m.attachments.map((a) => (
                            <button
                              key={a.id}
                              type="button"
                              className="btn btn-default btn-sm"
                              title={`Open ${a.filename}`}
                              onClick={() => downloadOrViewAttachment(a.id, a.filename, false)}
                            >
                              <i className="fa fa-paperclip" style={{ marginRight: "6px" }} />
                              {a.filename}
                              {a.sizeBytes ? ` (${Math.round(a.sizeBytes / 1024)} KB)` : ""}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : (
              <>
                <div className="sub-email-message">
                  <div className="sub-email-message-head">
                    <div className="sub-email-people">
                      <div>
                        <strong>{mail.from_email || "-"}</strong>
                      </div>
                      <div className="text-muted sub-email-small">to {sentTo || "-"}</div>
                    </div>
                    <span className="text-muted sub-email-small sub-email-date">
                      {mail.sent_at || "-"}
                      {mail.status ? ` · ${mail.status}` : ""}
                    </span>
                  </div>

                  {sentDetail?.htmlContent ? (
                    <EmailBodyFrame html={readableTemplate(sentDetail.htmlContent)} />
                  ) : (
                    <p className="text-center text-muted m-0 py-3">
                      Email content is not available.
                    </p>
                  )}

                  {(sentDetail?.attachmentName || sentDetail?.attachmentLink) && (
                    <div className="sub-email-attachments">
                      {sentDetail.attachmentLink ? (
                        <a
                          href={sentDetail.attachmentLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-default btn-sm"
                        >
                          <i className="fa fa-paperclip" style={{ marginRight: "6px" }} />
                          {sentDetail.attachmentName || "Attachment"}
                        </a>
                      ) : (
                        <span className="text-muted sub-email-small">
                          <i className="fa fa-paperclip" style={{ marginRight: "6px" }} />
                          {sentDetail.attachmentName}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {sentEvents.length > 0 && (
                  <table className="table table-condensed bg-white mb-0">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentEvents.map((event, index) => (
                        <tr key={index}>
                          <td style={{ textTransform: "capitalize" }}>
                            {(event.event_type || "-").toLowerCase()}
                          </td>
                          <td>{formatDateTime(event.event_time) || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailViewPopup;
